// Datos de filiación del Anexo 1 MSP (formulario 001, bloques C y D; páginas 4-5 del PDF, spec en
// docs/hce-anexos/001-admision-alta-egreso.md): catálogos de la "LISTA DE CATEGORÍAS" del papel y las conversiones puras
// entre el formulario de Datos Demográficos y el `Patient`. Separado de `DatosDemograficosTab.tsx` para poder probarlo.
//
// Compatibilidad hacia atrás (los pacientes ya guardados no se tocan):
// - Un valor guardado que ya no está en el catálogo (p. ej. "Postgrado", "Otro") sigue mostrándose como opción "(anterior)".
// - Nombre: el `family` y el `given` siguen siendo los mismos campos de FHIR; el primer/segundo apellido se guardan además como
//   extensiones del propio `HumanName` (`primer-apellido` / `segundo-apellido`). Un apellido sin esas extensiones (datos
//   viejos) se lee completo como "primer apellido" y se vuelve a guardar igual, sin partirlo por adivinanza.
// - Teléfonos: fijo = `telecom` phone con `use: home`, celular = `use: mobile`; el celular va primero para que quien lea "el
//   primer teléfono" (recordatorios, WhatsApp) siga recibiendo el celular. Un teléfono viejo sin `use` se lee como celular.
import type { Address, ContactPoint, HumanName, Patient } from '@medplum/fhirtypes';

export interface OpcionCatalogo {
  readonly codigo: string;
  readonly etiqueta: string;
}

const lista = (...etiquetas: string[]): OpcionCatalogo[] => etiquetas.map((e) => ({ codigo: e, etiqueta: e }));

// ---------- Catálogos del papel ("LISTA DE CATEGORÍAS", hoja EGRESO) ----------

export const ESTADOS_CIVILES: readonly OpcionCatalogo[] = lista('Soltero/a', 'Casado/a', 'Divorciado/a', 'Viudo/a', 'Unido/a', 'Unión de hecho', 'No aplica');

export const NACIONALIDADES: readonly OpcionCatalogo[] = lista(
  'Ecuatoriana', 'Colombiana', 'Peruana', 'Estadounidense', 'Venezolana', 'Panameña', 'Española', 'Argentina', 'Chilena', 'Cubana',
  'Holandesa', 'Mexicana', 'Brasileña', 'Salvadoreña', 'Canadiense', 'Alemana', 'China', 'Británica', 'Francesa', 'Otra',
);

export const NIVELES_EDUCACION: readonly OpcionCatalogo[] = lista(
  'No aplica', 'Ninguno', 'Inicial', 'Educación Básica (preparatoria)', 'Educación Básica (Elemental o media)', 'Educación Básica (Superior)', 'Bachillerato', 'Técnico Superior', 'Tercer Nivel', 'Cuarto Nivel',
);

export const NACIONALIDADES_ETNICAS: readonly OpcionCatalogo[] = lista('No aplica', 'Achuar', 'Awa', 'Cofán', 'Chachi', 'Epera', 'Kichwa', 'Secoya', 'Shuar', 'Shiwiar', 'Siona', 'Tsáchila', 'Waorani', 'Zapara', 'Andoa');

/** Solo aplica a nacionalidad étnica Kichwa. */
export const PUEBLOS: readonly OpcionCatalogo[] = lista('No aplica', 'Chibuleo', 'Huancavilca', 'Karanki', 'Kañari', 'Kayambi', 'Kisapincha', 'Kitukara', 'Manta', 'Natabuela', 'Otavalo', 'Paltas', 'Panzaleo');

/** Los códigos son los de siempre (se guardan en el CodeSystem `autoidentificacion-etnica`); "otro" queda solo como valor anterior. */
export const AUTOIDENTIFICACION_ETNICA: readonly OpcionCatalogo[] = [
  { codigo: 'no-aplica', etiqueta: 'No aplica' },
  { codigo: 'indigena', etiqueta: 'Indígena' },
  { codigo: 'afroecuatoriano', etiqueta: 'Afroecuatoriano/a' },
  { codigo: 'negro', etiqueta: 'Negro/a' },
  { codigo: 'mulato', etiqueta: 'Mulato/a' },
  { codigo: 'montubio', etiqueta: 'Montubio/a' },
  { codigo: 'mestizo', etiqueta: 'Mestizo/a' },
  { codigo: 'blanco', etiqueta: 'Blanco/a' },
  { codigo: 'no-sabe', etiqueta: 'No sabe/No responde' },
];
const ETIQUETAS_ANTERIORES: Readonly<Record<string, string>> = { otro: 'Otro' };

export const ESTADOS_EDUCACION: readonly OpcionCatalogo[] = [
  { codigo: 'no-aplica', etiqueta: 'No aplica' },
  { codigo: 'cursando', etiqueta: 'Cursando' },
  { codigo: 'completa', etiqueta: 'Completa' },
  { codigo: 'incompleta', etiqueta: 'Incompleta' },
];

export const TIPOS_EMPRESA: readonly OpcionCatalogo[] = [
  { codigo: 'no-aplica', etiqueta: 'No aplica' },
  { codigo: 'publica', etiqueta: 'Pública' },
  { codigo: 'privada', etiqueta: 'Privada' },
  { codigo: 'ninguno', etiqueta: 'Ninguno' },
];

export const TIPOS_BONO: readonly OpcionCatalogo[] = [
  { codigo: 'ninguno', etiqueta: 'Ninguno' },
  { codigo: 'bdh', etiqueta: 'Desarrollo Humano' },
  { codigo: 'joaquin-gallegos-lara', etiqueta: 'Joaquín Gallegos' },
  { codigo: 'manuela-espejo', etiqueta: 'Manuela Espejo' },
];

/**
 * Las opciones del catálogo más, si el valor guardado ya no está en él (dato anterior al papel), ese valor como "(anterior)":
 * abrir y guardar la ficha no borra ni cambia lo que ya tenía el paciente.
 */
export function opcionesConAnterior(opciones: readonly OpcionCatalogo[], actual: string): OpcionCatalogo[] {
  if (!actual || opciones.some((o) => o.codigo === actual)) return [...opciones];
  return [...opciones, { codigo: actual, etiqueta: `${ETIQUETAS_ANTERIORES[actual] ?? actual} (anterior)` }];
}

// ---------- Nombre: primer/segundo apellido y primer/segundo nombre ----------

export const EXT_PRIMER_APELLIDO = 'http://logiacare.ec/fhir/extension/primer-apellido';
export const EXT_SEGUNDO_APELLIDO = 'http://logiacare.ec/fhir/extension/segundo-apellido';

export interface NombreDesglosado {
  primerNombre: string;
  segundoNombre: string;
  primerApellido: string;
  segundoApellido: string;
}

export function nombreDesdePatient(patient: Patient): NombreDesglosado {
  const n = patient.name?.[0];
  const given = n?.given ?? [];
  const propio = n?.extension?.find((e) => e.url === EXT_PRIMER_APELLIDO)?.valueString;
  const materno = n?.extension?.find((e) => e.url === EXT_SEGUNDO_APELLIDO)?.valueString;
  const family = n?.family ?? '';
  return {
    primerNombre: given[0] ?? '',
    segundoNombre: given.slice(1).join(' '),
    // Dato con extensiones: tal cual. Dato viejo: todo el apellido como primer apellido (no se adivina dónde se parte).
    primerApellido: propio !== undefined || materno !== undefined ? (propio ?? '') : family,
    segundoApellido: propio !== undefined || materno !== undefined ? (materno ?? '') : '',
  };
}

export function nombreParaPatient(n: NombreDesglosado): HumanName {
  const given = [n.primerNombre.trim(), ...n.segundoNombre.trim().split(/\s+/)].filter(Boolean);
  const primer = n.primerApellido.trim();
  const segundo = n.segundoApellido.trim();
  const family = [primer, segundo].filter(Boolean).join(' ');
  const extension = [
    ...(primer ? [{ url: EXT_PRIMER_APELLIDO, valueString: primer }] : []),
    ...(segundo ? [{ url: EXT_SEGUNDO_APELLIDO, valueString: segundo }] : []),
  ];
  return {
    given: given.length ? given : undefined,
    family: family || undefined,
    // Solo cuando hay segundo apellido: con uno solo, `family` ya lo dice todo (y un dato viejo no gana extensiones sin cambio).
    ...(segundo && extension.length ? { extension } : {}),
  };
}

// ---------- Teléfonos: fijo y celular ----------

export interface Telefonos {
  fijo: string;
  celular: string;
}

/** Índices (en `telecom`) del teléfono que se muestra como fijo y del que se muestra como celular; el resto de teléfonos no se tocan. */
function indicesTelefonos(telecom: readonly ContactPoint[]): { fijo: number; celular: number } {
  const esTelefono = (c: ContactPoint): boolean => c.system === 'phone' && !!c.value;
  const fijo = telecom.findIndex((c) => esTelefono(c) && c.use === 'home');
  let celular = telecom.findIndex((c) => esTelefono(c) && c.use === 'mobile');
  // Sin `use` (dato viejo de un solo teléfono) o con otro `use` (work, temp...): el primero que no sea el fijo se lee como celular.
  if (celular < 0) celular = telecom.findIndex((c, i) => esTelefono(c) && i !== fijo && c.use !== 'home');
  return { fijo, celular };
}

export function telefonosDesdePatient(patient: Patient): Telefonos {
  const telecom = patient.telecom ?? [];
  const { fijo, celular } = indicesTelefonos(telecom);
  return { fijo: telecom[fijo]?.value ?? '', celular: telecom[celular]?.value ?? '' };
}

/**
 * `telecom` con celular primero, luego fijo y correo. Conserva lo que el paciente ya tuviera y la ficha no muestra: otros sistemas
 * (fax, url...) y teléfonos adicionales (un segundo número, uno con `use: work`...), para que guardar no los borre.
 */
export function telecomParaPatient(previo: readonly ContactPoint[] | undefined, telefonos: Telefonos, email: string): ContactPoint[] {
  const telecom = previo ?? [];
  const { fijo, celular } = indicesTelefonos(telecom);
  const otros = telecom.filter((c, i) => i !== fijo && i !== celular && c.system !== 'email');
  return [
    ...(telefonos.celular.trim() ? [{ system: 'phone' as const, use: 'mobile' as const, value: telefonos.celular.trim() }] : []),
    ...(telefonos.fijo.trim() ? [{ system: 'phone' as const, use: 'home' as const, value: telefonos.fijo.trim() }] : []),
    ...(email.trim() ? [{ system: 'email' as const, value: email.trim() }] : []),
    ...otros,
  ];
}

// ---------- Dirección del contacto de emergencia ----------

export function direccionContacto(texto: string): Address | undefined {
  const t = texto.trim();
  return t ? { text: t } : undefined;
}
